import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Link,
  NavLink,
  Outlet,
  useMatch,
  useResolvedPath,
} from 'react-router-dom';
import useMatchMedia from 'hooks/useMatchMedia';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import DealContext from '../../providers/Deal/Deal.context';
import ListingContext from '../../providers/Listing/Listing.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import InterestToggle from './components/InterestToggle';
import GuideTour from 'providers/GuideTour/GuideTour.provider';
import { CONFIGURE_SELECTOR, GUIDE_STEP_NAMES, getGuideSteps } from './data';
import WorkflowHeader from './components/WorkflowHeader';
import WorkflowGuideAnchor from './components/WorkflowGuideAnchor';
import WorkflowPopupNotification from '../../providers/WorkflowPopupNotification/WorkflowPopupNotification.provider';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import {
  AUTOMATION_RELEASE_MODE,
  AutomationTourClosedEventProps,
  AutomationTourCompletedEventProps,
  AutomationTourStartedEventProps,
  AutomationTourStepViewedEventProps,
} from 'models/MixpanelEvents';
import { getArrayLength } from 'utils';
import { FILES_AVAILABILITY_MODE } from 'models/WorkflowConfiguration';

import './WorkflowConfiguration.styles.scss';
import './WorkflowConfiguration.styles.responsive.scss';

const WorkflowConfiguration: React.FC = () => {
  const { currentUser, updatePreferences } = useContext(CurrentUserContext);
  const { leadStagesCopies, dealRoom } = useContext(DealContext);
  const { canAccessDataRoom, workflowConfiguration } =
    useContext(ListingContext);

  const { _id: dealId, name: dealName } = dealRoom || {};

  const { filesAvailabilityMode } = workflowConfiguration || {};

  const getMixpanelProps = useGetMixpanelProps();

  const { seenAutomationFlow } = currentUser || {};

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const guideSteps = useMemo(
    () => getGuideSteps(isAbovePhablet, canAccessDataRoom),
    [isAbovePhablet, canAccessDataRoom],
  );

  const onTourStart = useCallback(
    () =>
      mixpanelService.automationTourStarted(
        getMixpanelProps({
          dealId,
          dealName,
          releaseMode:
            AUTOMATION_RELEASE_MODE[
              filesAvailabilityMode || FILES_AVAILABILITY_MODE.ALL_AT_ONCE
            ],
        }) as AutomationTourStartedEventProps,
      ),
    [dealId, dealName, filesAvailabilityMode, getMixpanelProps],
  );

  const onStepView = useCallback(
    (stepIndex: number) =>
      mixpanelService.automationTourStepViewed(
        getMixpanelProps({
          dealId,
          dealName,
          stepNumber: stepIndex + 1,
          stepName: GUIDE_STEP_NAMES[guideSteps[stepIndex]?.selector],
        }) as AutomationTourStepViewedEventProps,
      ),
    [dealId, dealName, getMixpanelProps, guideSteps],
  );

  const onTourComplete = useCallback(
    () =>
      mixpanelService.automationTourCompleted(
        getMixpanelProps({
          dealId,
          dealName,
        }) as AutomationTourCompletedEventProps,
      ),
    [dealId, dealName, getMixpanelProps],
  );

  const onTourClose = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= getArrayLength(guideSteps) - 1) return;

      mixpanelService.automationTourClosed(
        getMixpanelProps({
          dealId,
          dealName,
          stepNumber: stepIndex + 1,
        }) as AutomationTourClosedEventProps,
      );
    },
    [dealId, dealName, getMixpanelProps, guideSteps],
  );

  // The first visit stops the Automation tab pulsing.
  useEffect(() => {
    if (seenAutomationFlow) return;

    updatePreferences({ seenAutomationFlow: true });
  }, [seenAutomationFlow, updatePreferences]);

  const { t } = useTranslation();

  const { pathname: basePath } = useResolvedPath('');
  const stageMatch = useMatch(`${basePath}/:stage`);
  const activeStage = stageMatch?.params.stage as LeadStages;

  // Mobile shows one step at a time: the stage list, or the selected stage.
  const showStageList = isAbovePhablet || !activeStage;
  const showStageContent = isAbovePhablet || !!activeStage;

  const classes = classNames('workflow-configuration', 'white-panel');

  // Desktop titles the page with the open stage, mobile's list stays "Automation".
  const headerTitle =
    isAbovePhablet && activeStage
      ? t(leadStagesCopies[activeStage])
      : t('automation');

  return (
    <GuideTour
      guideSteps={guideSteps}
      onTourStart={onTourStart}
      onStepView={onStepView}
      onTourComplete={onTourComplete}
      onTourClose={onTourClose}
    >
      <WorkflowPopupNotification>
        <div className={classes}>
          {showStageList ? (
            <WorkflowHeader title={headerTitle} />
          ) : (
            <Link to="." className="workflow-configuration__back-header">
              <ArrowleftIcon />
              <span>{t(leadStagesCopies[activeStage])}</span>
            </Link>
          )}

          <div className="workflow-configuration__content">
            {showStageList && (
              <aside>
                <nav>
                  <p className="wft-title">
                    {t('leadStages')}
                    <WorkflowGuideAnchor
                      id={CONFIGURE_SELECTOR}
                      title="configureLeadStages"
                      description="configureLeadStagesDescription"
                    />
                  </p>

                  <ul className="workflow-configuration__menu">
                    {Object.values(LEAD_STAGES).map((step, index) => (
                      <li key={step}>
                        <NavLink
                          className={({ isActive }) =>
                            classNames({ active: isActive })
                          }
                          to={step}
                          replace={isAbovePhablet}
                        >
                          <div className="workflow-configuration__link-info">
                            <p>{`${t('stage')} ${index + 1}`}</p>
                            <p>{t(leadStagesCopies[step])}</p>
                          </div>
                          {isAbovePhablet ? (
                            <CheckcircleIcon />
                          ) : (
                            <ArrowrightIcon />
                          )}
                        </NavLink>

                        <InterestToggle step={step} />
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="../qualification-questions"
                    className="workflow-configuration__qualification-link"
                  >
                    {t('seeQualificationQuestions')}
                  </Link>
                </nav>
              </aside>
            )}

            {showStageContent && (
              <main>
                <Outlet />
              </main>
            )}
          </div>
        </div>
      </WorkflowPopupNotification>
    </GuideTour>
  );
};

export default WorkflowConfiguration;
